import type { SnapshotEnvelope } from './contract';
import { AuthCoordinationError, captureAuthEpoch, isCurrentAuthEpoch, isAuthUnknown, markAuthUnknown, publishAuthChange, withCookieMutation } from '../app/auth-context';

const API_ROOT = '/api/v1';
const SECURE_FAILURE_EVENT = 'kazna:secure-failure';
const WEB_INSTALLATION_KEY = 'kazna:web-installation';
export const FINANCE_CONTRACTS = 'mobile-finance-v3,mobile-finance-v2';
const CONTRACT_FAILURE_EVENT = 'kazna:contract-failure';
const SUBSCRIPTION_FAILURE_EVENT = 'kazna:subscription-required';
const SUBSCRIPTION_CONFIRMED_EVENT = 'kazna:subscription-confirmed';
export function onSubscriptionRequired(listener: () => void): () => void {
  window.addEventListener(SUBSCRIPTION_FAILURE_EVENT, listener);
  return () => window.removeEventListener(SUBSCRIPTION_FAILURE_EVENT, listener);
}
export function onSubscriptionConfirmed(listener: () => void): () => void {
  window.addEventListener(SUBSCRIPTION_CONFIRMED_EVENT, listener);
  return () => window.removeEventListener(SUBSCRIPTION_CONFIRMED_EVENT, listener);
}
export function confirmSubscriptionRead(epoch: string): void {
  assertAuthEpoch(epoch);
  window.dispatchEvent(new Event(SUBSCRIPTION_CONFIRMED_EVENT));
}

// Optional capability discovery must preserve old-server compatibility without
// treating a missing public route as loss of access to the signed-in account.
export async function subscriptionCapabilities(epoch: string): Promise<boolean> {
  try {
    const result = await request<{ data: { subscriptions?: { enabled: boolean } } }>('/capabilities', { method: 'GET' }, false, true, false, false, epoch);
    return result.data.subscriptions?.enabled === true;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return false;
    throw error;
  }
}

export function subscriptionWrite<T>(path: string, body: unknown, key: string, epoch: string): Promise<T> {
  if (!/^\/me\/subscription\/(trial|purchases(?:\/[a-f0-9-]{36}\/mock-confirm)?)$/iu.test(path) || !/^[a-f0-9-]{36}$/iu.test(key)) throw new Error('Недопустимый запрос подписки');
  return request<T>(path, { method: 'POST', headers: { 'Idempotency-Key': key, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, false, true, false, false, epoch);
}

export function subscriptionRead<T>(epoch: string, signal?: AbortSignal): Promise<T> {
  return request<T>('/me/subscription', { method: 'GET', signal }, false, true, false, false, epoch);
}

export function onContractFailure(listener: () => void): () => void {
  window.addEventListener(CONTRACT_FAILURE_EVENT, listener);
  return () => window.removeEventListener(CONTRACT_FAILURE_EVENT, listener);
}

export function requireFinanceV3(value: unknown, epoch = captureAuthEpoch()): asserts value is SnapshotEnvelope {
  assertAuthEpoch(epoch);
  const envelope = value as { schemaVersion?: unknown; contractId?: unknown; snapshot?: { debtCategories?: unknown; debtMovements?: unknown } };
  if (envelope?.schemaVersion !== 3 || envelope.contractId !== 'mobile-finance-v3' || !Array.isArray(envelope.snapshot?.debtCategories) || !Array.isArray(envelope.snapshot?.debtMovements)) {
    window.dispatchEvent(new Event(CONTRACT_FAILURE_EVENT));
    secureFailure();
    throw new ApiError(409, 'FINANCE_CONTRACT_UNSUPPORTED', 'Обновите приложение или сервер для работы с долгами');
  }
}

// Signed URLs are finance requests too. Keep bytes in memory only and revoke
// the object URL once the browser has accepted the download.
export async function apiDownload(pathOrUrl: string, filename: string, epoch = captureAuthEpoch(), assertOwner?: () => void): Promise<void> {
  assertAuthEpoch(epoch, assertOwner);
  const url = new URL(pathOrUrl, window.location.origin);
  // Public API URLs may use the landing/API host. Resolve only the documented
  // download path through our same-origin ingress; never send cookies elsewhere.
  if (!url.pathname.startsWith(`${API_ROOT}/exports/download/`)) throw new Error('Недопустимая ссылка экспорта');
  const blob = await request<Blob>(`${url.pathname.slice(API_ROOT.length)}${url.search}`, { method: 'GET' }, true, true, true, false, epoch, assertOwner);
  assertAuthEpoch(epoch, assertOwner);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly retryAfterMs?: number,
  ) {
    super(message);
  }
}

let refreshFlight: { epoch: string; promise: Promise<void> } | undefined;

export function onSecureFailure(listener: () => void): () => void {
  window.addEventListener(SECURE_FAILURE_EVENT, listener);
  return () => window.removeEventListener(SECURE_FAILURE_EVENT, listener);
}

export async function apiGet<T>(path: string, epoch = captureAuthEpoch(), assertOwner?: () => void): Promise<T> {
  return request<T>(path, { method: 'GET' }, true, true, false, false, epoch, assertOwner);
}

// This read proves only possession of an email-action cookie. Missing proof
// must not refresh or invalidate an independently authenticated account.
export async function emailActionGet<T>(actionId: string, epoch = captureAuthEpoch()): Promise<T> {
  if (!/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/iu.test(actionId)) throw new Error('Недопустимое подтверждение');
  return request<T>(`/auth/web/email/actions/${actionId}`, { method: 'GET', mode: 'same-origin' }, false, false, false, true, epoch);
}

// A flow owns its key in memory: reuse it for an unchanged retry, and create a
// new key after an explicit submit of changed input. Never fingerprint secrets.
export function createAuthRequestKey(): string {
  return crypto.randomUUID();
}

export async function authMutation<T>(path: string, body: unknown, key: string, epoch = captureAuthEpoch(), assertOwner?: () => void): Promise<T> {
  return authWrite<T>(path, body, key, epoch, 'POST', assertOwner);
}

// Cancel is a cookie mutation too; never use persisted financial fingerprints.
export async function authDelete(path: string, key: string, epoch = captureAuthEpoch(), assertOwner?: () => void): Promise<void> {
  if (!/^\/auth\/web\/reauth\/yandex\/challenges\/[a-f0-9-]{36}$/iu.test(path)) throw new Error('Недопустимый путь отмены подтверждения');
  return authWrite<void>(path, undefined, key, epoch, 'DELETE', assertOwner);
}

async function authWrite<T>(path: string, body: unknown, key: string, epoch: string, method: 'POST' | 'DELETE', assertOwner?: () => void): Promise<T> {
  const browserAuthPath = /^\/auth\/web\/[A-Za-z0-9/_-]+$/u.test(path);
  const emailLinkPath = path === '/me/identities/email/link-requests' || path === '/me/identities/email/link/complete';
  if (!browserAuthPath && !emailLinkPath) throw new Error('Недопустимый путь авторизации');
  if (key.length < 8 || key.length > 160 || /[\r\n]/u.test(key)) throw new Error('Недопустимый ключ запроса авторизации');
  return request<T>(path, {
    method,
    mode: 'same-origin',
    headers: {
      'Idempotency-Key': key,
      'X-Kazna-Installation-Id': getWebInstallationId(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  }, false, false, false, true, epoch, assertOwner);
}

export async function apiMutation<T>(
  path: string,
  options: {
    method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    scope: string;
  } & (
    | { version?: undefined; createOnly?: undefined }
    | { version: number | string; createOnly?: undefined }
    | { version?: undefined; createOnly: true }
  ),
  epoch = captureAuthEpoch(),
  assertOwner?: () => void,
): Promise<T> {
  assertAuthEpoch(epoch, assertOwner);
  const bodyText = options.body === undefined ? undefined : JSON.stringify(options.body);
  const idempotency = await resolveIdempotencyKey(options.scope, bodyText ?? '', epoch, assertOwner);
  const result = await request<T>(
      path,
      {
        method: options.method ?? 'POST',
        headers: {
          'Idempotency-Key': idempotency.key,
          ...(options.version === undefined
            ? {}
            : { 'If-Match': formatIfMatch(options.version) }),
          ...(options.createOnly === true ? { 'If-None-Match': '*' } : {}),
          ...(bodyText === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body: bodyText,
      },
      false,
      true,
      false,
      false,
      epoch,
      assertOwner,
    );
  // A successful auth exchange rotates the epoch itself; only ownership is
  // still required here before removing this operation's idempotency metadata.
  assertOwner?.();
  const stored = safeJson(localStorage.getItem(idempotency.storageKey));
  if (stored?.key === idempotency.key) localStorage.removeItem(idempotency.storageKey);
  return result;
}

// Capture once from the owning provider, never reacquire identity after await.
// Protected auth/link/reauth callers use this same bound authMutation seam.
// An optional intent guard also runs inside the cookie lock at actual dispatch.
export function createAccountApi(epoch: string, assertOwner?: () => void) {
  return {
    epoch,
    assertCurrent: () => assertAuthEpoch(epoch, assertOwner),
    apiGet: <T,>(path: string) => apiGet<T>(path, epoch, assertOwner),
    apiMutation: <T,>(path: string, options: Parameters<typeof apiMutation>[1]) => apiMutation<T>(path, options, epoch, assertOwner),
    apiDownload: (path: string, filename: string) => apiDownload(path, filename, epoch, assertOwner),
    authMutation: <T,>(path: string, body: unknown, key: string) => authMutation<T>(path, body, key, epoch, assertOwner),
    authDelete: (path: string, key: string) => authDelete(path, key, epoch, assertOwner),
    requireFinanceV3: (value: unknown): void => { assertOwner?.(); requireFinanceV3(value, epoch); },
  };
}

export function formatIfMatch(version: number | string): string {
  const normalized = String(version).trim().replace(/^"|"$/gu, '');
  if (!/^[1-9]\d*$/u.test(normalized)) {
    throw new Error('If-Match version must be a positive integer');
  }
  return `"${normalized}"`;
}

async function request<T>(
  path: string,
  init: RequestInit,
  isRead: boolean,
  allowRefresh = true,
  asBlob = false,
  authRequest = false,
  epoch = captureAuthEpoch(),
  assertOwner?: () => void,
): Promise<T> {
  const changesCookies = init.method !== 'GET' && (path.startsWith('/auth/web/') || path === '/me/identities/email/link/complete');
  const changesSession = path === '/auth/web/email/login' || path === '/auth/web/logout'
    || /^\/auth\/web\/yandex\/challenges\/[^/]+\/exchange$/u.test(path);
  const work = async () => {
    assertAuthEpoch(epoch, assertOwner);
    if (isAuthUnknown() && !path.startsWith('/auth/web/')) {
      throw new ApiError(409, 'AUTH_STATE_UNKNOWN', 'Вход не подтверждён. Повторите вход, чтобы открыть финансовые данные.');
    }
    try {
      const result = await performRequest<T>(path, init, isRead, allowRefresh && !changesCookies, asBlob, authRequest, epoch, changesCookies, assertOwner);
      // A dispatched session write may have applied Set-Cookie even if its
      // intent was replaced meanwhile. Publish that change before the caller
      // rejects stale ownership, so no tab keeps the former account's cache.
      assertAuthEpoch(epoch);
      if (changesSession) publishAuthChange();
      return result;
    } catch (error) {
      if ((changesSession || path === '/auth/web/refresh') && error instanceof ApiError && error.status === 0 && isCurrentAuthEpoch(epoch)) markAuthUnknown();
      throw error;
    }
  };
  return changesCookies ? withCookieMutation(work) : work();
}

function assertAuthEpoch(epoch: string, assertOwner?: () => void): void {
  if (!isCurrentAuthEpoch(epoch)) throw new ApiError(409, 'AUTH_EPOCH_CHANGED', 'Аккаунт изменился. Повторите действие.');
  assertOwner?.();
}

async function performRequest<T>(
  path: string, init: RequestInit, isRead: boolean, allowRefresh: boolean,
  asBlob: boolean, authRequest: boolean, epoch: string, cookieLockHeld: boolean,
  assertOwner?: () => void,
): Promise<T> {
  if (!navigator.onLine) {
    secureFailure();
    throw new ApiError(0, 'OFFLINE', 'Нет подключения к интернету');
  }
  let response: Response;
  try {
    const send = () => {
      // A request queued with A's intent must never be sent with B's cookies.
      assertAuthEpoch(epoch, assertOwner);
      return fetch(`${API_ROOT}${path}`, {
        ...init,
        credentials: 'include',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          ...(/^\/workspaces\/[^/]+\//u.test(path) || path.startsWith('/exports/download/') ? { 'X-Accept-Compatibility-Contracts': FINANCE_CONTRACTS } : {}),
          ...init.headers,
        },
      });
    };
    // Ordinary requests share the dispatch gate through response headers only.
    // Their body may arrive late; epoch checks discard it. Release before 401
    // refresh to avoid nesting locks. Unsupported browsers cannot mutate cookies
    // at all; their existing-session reads retain ordinary fail-closed behavior.
    response = cookieLockHeld || !navigator.locks?.request ? await send() : await withCookieMutation(send);
    // Drain even errors/204 before releasing the cross-tab cookie lock. The
    // reconstructed response also makes a body-stream failure a network error.
    const bytes = await response.arrayBuffer();
    response = new Response(response.status === 204 || response.status === 205 || response.status === 304 ? null : bytes, {
      status: response.status, statusText: response.statusText, headers: response.headers,
    });
  } catch (error) {
    assertAuthEpoch(epoch);
    // Preserve a guard rejection before dispatch. A real network failure after
    // dispatch must retain NETWORK_ERROR/unknown-session handling instead.
    if (error instanceof ApiError) throw error;
    if (init.signal?.aborted) throw new ApiError(0, 'REQUEST_CANCELLED', 'Запрос отменён');
    secureFailure();
    throw new ApiError(0, 'NETWORK_ERROR', 'Сервер недоступен');
  }
  assertAuthEpoch(epoch);
  if (init.signal?.aborted) throw new ApiError(0, 'REQUEST_CANCELLED', 'Запрос отменён');
  if (response.status === 401 && !path.startsWith('/auth/web/') && allowRefresh) {
    try {
      await refreshSession(epoch, assertOwner);
      assertAuthEpoch(epoch);
      return request<T>(path, init, isRead, false, asBlob, authRequest, epoch, assertOwner);
    } catch (error) {
      assertAuthEpoch(epoch);
      secureFailure();
      if (error instanceof AuthCoordinationError) throw error;
      throw new ApiError(401, 'UNAUTHENTICATED', 'Сессия завершена');
    }
  }
  if (!response.ok) {
    const failure = await parseFailure(response);
    assertAuthEpoch(epoch);
    if (failure.status === 402 && failure.code === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new Event(SUBSCRIPTION_FAILURE_EVENT));
      throw failure;
    }
    if (failure.code === 'FINANCE_CONTRACT_UNSUPPORTED') window.dispatchEvent(new Event(CONTRACT_FAILURE_EVENT));
    const staleHistoryCursor = isRead && response.status === 400 && failure.code === 'INVALID_CURSOR' && /^\/workspaces\/[^/]+\/history\?/u.test(path) && new URLSearchParams(path.split('?')[1]).has('cursor');
    const expectedAuthRejection = authRequest && isExpectedAuthRejection(path, failure);
    if (!expectedAuthRejection && ((isRead && !staleHistoryCursor) || response.status === 401 || response.status === 403 || failure.code === 'FINANCE_CONTRACT_UNSUPPORTED')) secureFailure();
    throw failure;
  }
  if (response.status === 204 || (response.status === 202 && path === '/me' && init.method === 'DELETE')) return undefined as T;
  if (asBlob) return await response.blob() as T;
  return (await response.json()) as T;
}

async function refreshSession(epoch: string, assertOwner?: () => void): Promise<void> {
  if (refreshFlight?.epoch === epoch) return refreshFlight.promise;
  const promise = (async () => {
    await request<void>('/auth/web/refresh', {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      // The backend rotates a refresh token on every successful request and
      // revokes its whole family if the same token is retried with another
      // idempotency key. A stable key per web installation makes concurrent
      // refreshes from Safari/PWA windows replay the same rotation safely.
      headers: { 'Idempotency-Key': `web-refresh:${getWebInstallationId()}` },
    }, false, false, false, false, epoch, assertOwner);
  })().finally(() => {
    if (refreshFlight?.promise === promise) refreshFlight = undefined;
  });
  refreshFlight = { epoch, promise };
  return promise;
}

export function getWebInstallationId(): string {
  const stored = localStorage.getItem(WEB_INSTALLATION_KEY);
  if (stored !== null && stored.length > 0) return stored;
  const installationId = crypto.randomUUID();
  localStorage.setItem(WEB_INSTALLATION_KEY, installationId);
  return installationId;
}

async function parseFailure(response: Response): Promise<ApiError> {
  const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'));
  try {
    const value = (await response.json()) as {
      error?: { code?: string; message?: string };
    };
    return new ApiError(
      response.status,
      value.error?.code ?? 'REQUEST_FAILED',
      value.error?.message ?? 'Не удалось выполнить запрос',
      retryAfterMs,
    );
  } catch {
    return new ApiError(response.status, 'REQUEST_FAILED', 'Не удалось выполнить запрос', retryAfterMs);
  }
}

function isExpectedAuthRejection(path: string, failure: ApiError): boolean {
  const login = path === '/auth/web/email/login';
  const action = [
    '/auth/web/email/actions/open',
    '/auth/web/email/actions/confirm',
    '/auth/web/email/registration/complete',
    '/auth/web/email/password-reset/complete',
  ].includes(path) || /^\/auth\/web\/email\/actions\/[a-f0-9-]{36}$/iu.test(path);
  return ((login || action) && failure.status === 401 && failure.code === 'UNAUTHENTICATED')
    || ((login || action) && failure.status === 410 && failure.code === 'EMAIL_ACTION_UNAVAILABLE')
    || (action && failure.status === 409 && failure.code === 'EMAIL_ACTION_CONTEXT_CHANGED')
    || (login && failure.status === 409 && failure.code === 'AUTH_STATE_CHANGED');
}

function parseRetryAfter(value: string | null): number | undefined {
  if (value === null) return undefined;
  const header = value.trim();
  let delay: number;
  if (/^\d+(?:\.\d+)?$/u.test(header)) {
    delay = Number(header) * 1_000;
  } else if (/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} GMT$/u.test(header)) {
    const date = new Date(header);
    // Date.parse normalizes impossible dates such as February 31. Accept only
    // a real HTTP date, then clamp an elapsed cooldown to zero.
    if (date.toUTCString() !== header) return undefined;
    delay = Math.max(0, date.getTime() - Date.now());
  } else {
    return undefined;
  }
  return Number.isFinite(delay) && delay >= 0 ? delay : undefined;
}

function secureFailure(): void {
  window.dispatchEvent(new Event(SECURE_FAILURE_EVENT));
}

async function resolveIdempotencyKey(
  scope: string,
  body: string,
  epoch: string,
  assertOwner?: () => void,
): Promise<{ key: string; storageKey: string }> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
  assertAuthEpoch(epoch, assertOwner);
  const hash = Array.from(new Uint8Array(digest), value =>
    value.toString(16).padStart(2, '0'),
  ).join('');
  const storageKey = `kazna:idempotency:${scope}`;
  const previous = safeJson(localStorage.getItem(storageKey));
  if (previous?.hash === hash && typeof previous.key === 'string') {
    return { key: previous.key, storageKey };
  }
  const key = crypto.randomUUID();
  localStorage.setItem(storageKey, JSON.stringify({ key, scope, hash }));
  return { key, storageKey };
}

function safeJson(value: string | null): { key?: unknown; hash?: unknown } | undefined {
  if (value === null) return undefined;
  try {
    return JSON.parse(value) as { key?: unknown; hash?: unknown };
  } catch {
    return undefined;
  }
}
